import type { InternalRequestDetailResponse, ImportItemResult } from "@synthesis/shared-services/internal/application/types";

import {
  getDictionaryOptionLabel,
  type CreateApplicationDictionaries,
} from "../../pages/create-application-page/create-application-dictionaries.lib";
import { getInternalInitiatorDisplay } from "../../shared/utils/application.utils";

import type { ImportApplicationResultData, ImportResultAttachment, ImportResultStatusItem } from "./import-application-result-view";

export const isImportItemFailed = (item: ImportItemResult): boolean => item.outcome === "FAILED" || (item.errors?.length ?? 0) > 0;

export const hasSuccessfulImportItems = (items: ImportItemResult[]): boolean => items.some((item) => !isImportItemFailed(item));

/**
 * Собирает статусы из ответа импорта: previousStatus (до импорта) и newStatus (после).
 * Старые значения (previousStatus) показываются только при исходе UPDATED — в остальных
 * случаях (CREATED/NO_CHANGE/CONFLICT/FAILED) «до импорта» не отображаем.
 * Строка «Статус заявки» считается изменённой, если статусы отличаются
 * (isChanged выставляется на новом значении — для переключателя «Отобразить только изменения»).
 */
export const buildImportResultStatuses = (item: ImportItemResult): ImportResultStatusItem[] => {
  const statuses: ImportResultStatusItem[] = [];

  if (item.outcome === "UPDATED" && item.previousStatus?.trim()) {
    statuses.push({ id: `previous-${item.previousStatus}`, label: item.previousStatus, isChanged: false });
  }

  if (item.newStatus?.trim()) {
    statuses.push({
      id: `new-${item.newStatus}`,
      label: item.newStatus,
      isChanged: item.previousStatus !== item.newStatus,
    });
  }

  return statuses;
};

/**
 * Собирает вложения из ответа импорта: attachmentsAdded (добавленные импортом) и
 * attachmentsMissing (есть у заявки, но отсутствуют в архиве). Оба набора — изменения.
 */
export const buildImportResultAttachments = (item: ImportItemResult): ImportResultAttachment[] => [
  ...(item.attachmentsAdded ?? []).map((fileName, index) => ({ id: `added-${index}-${fileName}`, fileName, isChanged: true })),
  ...(item.attachmentsMissing ?? []).map((fileName, index) => ({ id: `missing-${index}-${fileName}`, fileName, isChanged: true })),
];

export const mapImportItemToResultData = (item: ImportItemResult): ImportApplicationResultData => ({
  outcome: item.outcome,
  number: item.number,
  title: item.title,
  statuses: buildImportResultStatuses(item),
  attachments: buildImportResultAttachments(item),
});

/**
 * Дотягивает недостающие поля (Тип заявки, Инициатор, Дорога, Дата создания, Вложения) из детальной
 * заявки, полученной по id после успешного импорта. Значения «до/после» для переключателя
 * «Отобразить только изменения» остаются из ответа импорта (statuses/attachments не трогаем):
 * вложения из детали добавляются к уже собранным (attachmentsAdded/attachmentsMissing),
 * не перетирая флаги изменений.
 */
export const enrichImportResultWithRequestDetail = (
  data: ImportApplicationResultData,
  detail: InternalRequestDetailResponse | undefined,
  dictionaries: CreateApplicationDictionaries,
): ImportApplicationResultData => {
  if (!detail) {
    return data;
  }

  const detailFiles: ImportResultAttachment[] = (detail.files ?? []).map((file) => ({
    id: file.id,
    fileName: [file.baseName, file.ext].filter(Boolean).join("."),
  }));

  const knownFileNames = new Set(data.attachments.map((attachment) => attachment.fileName));
  const extraAttachments = detailFiles.filter((file) => !knownFileNames.has(file.fileName));

  return {
    ...data,
    number: data.number ?? detail.number ?? null,
    title: data.title ?? detail.title ?? null,
    typeLabel: getDictionaryOptionLabel(dictionaries.requestSubjects, detail.requestSubject),
    initiatorDisplay: getInternalInitiatorDisplay(detail as unknown as Record<string, unknown>),
    railway: getDictionaryOptionLabel(dictionaries.railways, detail.railway),
    importedAt: detail.importedAt ?? null,
    attachments: [...data.attachments, ...extraAttachments],
  };
};
